import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'en' | 'fr' | 'de' | 'nl' | 'es' | 'it';

interface TranslationDictionary {
  [key: string]: string;
}

interface Translations {
  [language: string]: TranslationDictionary;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const defaultContext: LanguageContextType = {
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
};

export const LanguageContext = createContext<LanguageContextType>(defaultContext);

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  // Translation function
  const t = (key: string): string => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translation object
export const translations: Translations = {
  en: {
    // General UI
    'app.title': 'FELCO Selector',
    'app.subtitle': 'Find the perfect FELCO pruning shears for your needs',
    'app.footer': '© {year} FELCO. All rights reserved.',
    'btn.back': 'Back',
    'btn.reset': 'Start Over',
    'btn.viewProduct': 'View Product',
    'language.select': 'Select your language',
    'language.en': 'English',
    'language.fr': 'Français',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.es': 'Español',
    'language.it': 'Italiano',
    'results.perfect': 'Perfect Match',
    'results.alternative': 'Alternative Products',
    'results.features': 'Features:',
    'results.noMatch': 'We couldn\'t find a perfect match for your criteria:',
    'results.perfectMatch': 'These products perfectly match all your requirements:',
    'results.noMatchText': 'We couldn\'t find any products that perfectly match all your criteria.',
    'results.partialMatch': 'These products match your handedness but don\'t fully match all your criteria:',

    // Questions
    'question.handSize': 'What is the size of your hand?',
    'question.handSize.help': 'Choose the option that best describes your hand size',
    'option.small': 'Small',
    'option.medium': 'Medium',
    'option.large': 'Large',
    
    'question.handedness': 'Are you right-handed or left-handed?',
    'question.handedness.help': 'Select which hand you primarily use for pruning',
    'option.right': 'Right-handed',
    'option.left': 'Left-handed',
    
    'question.usageType': 'What will you mainly use the pruning shears for?',
    'question.usageType.help': 'Select the primary type of cutting or pruning you\'ll be doing',
    'option.arboristry_and_forestry': 'Arboristry & Forestry',
    'option.flowers_and_roses': 'Flowers & Roses',
    'option.fruit_nut_trees_and_orchards': 'Fruit/Nut Trees & Orchards',
    'option.grapes_and_vineyards': 'Grapes & Vineyards', 
    'option.hedges_and_bushes': 'Hedges & Bushes',
    'option.landscaping_and_municipalities': 'Landscaping & Municipalities',
    'option.nurseries': 'Nurseries',
    
    'question.userType': 'Are you a professional or home gardener?',
    'question.userType.help': 'This helps us recommend the right tool for your level of use',
    'option.professional': 'Professional',
    'option.amateur': 'Home gardener',
  },
  fr: {
    // General UI
    'app.title': 'Sélecteur FELCO',
    'app.subtitle': 'Trouvez les sécateurs FELCO parfaits pour vos besoins',
    'app.footer': '© {year} FELCO. Tous droits réservés.',
    'btn.back': 'Retour',
    'btn.reset': 'Recommencer',
    'btn.viewProduct': 'Voir le produit',
    'language.select': 'Sélectionnez votre langue',
    'language.en': 'English',
    'language.fr': 'Français',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.es': 'Español',
    'language.it': 'Italiano',
    'results.perfect': 'Correspondance parfaite',
    'results.alternative': 'Produits alternatifs',
    'results.features': 'Caractéristiques:',
    'results.noMatch': 'Nous n\'avons pas trouvé de correspondance parfaite pour vos critères:',
    'results.perfectMatch': 'Ces produits correspondent parfaitement à toutes vos exigences:',
    'results.noMatchText': 'Nous n\'avons pas trouvé de produits qui correspondent parfaitement à tous vos critères.',
    'results.partialMatch': 'Ces produits correspondent à votre main dominante mais ne correspondent pas entièrement à tous vos critères:',

    // Questions
    'question.handSize': 'Quelle est la taille de votre main?',
    'question.handSize.help': 'Choisissez l\'option qui décrit le mieux la taille de votre main',
    'option.small': 'Petite',
    'option.medium': 'Moyenne',
    'option.large': 'Grande',
    
    'question.handedness': 'Êtes-vous droitier ou gaucher?',
    'question.handedness.help': 'Sélectionnez la main que vous utilisez principalement pour tailler',
    'option.right': 'Droitier',
    'option.left': 'Gaucher',
    
    'question.usageType': 'À quoi utiliserez-vous principalement les sécateurs?',
    'question.usageType.help': 'Sélectionnez le type principal de coupe ou de taille que vous ferez',
    'option.arboristry_and_forestry': 'Arboriculture et foresterie',
    'option.flowers_and_roses': 'Fleurs et roses',
    'option.fruit_nut_trees_and_orchards': 'Arbres fruitiers/à noix et vergers',
    'option.grapes_and_vineyards': 'Raisins et vignobles',
    'option.hedges_and_bushes': 'Haies et buissons',
    'option.landscaping_and_municipalities': 'Aménagement paysager et municipalités',
    'option.nurseries': 'Pépinières',
    
    'question.userType': 'Êtes-vous un professionnel ou un jardinier amateur?',
    'question.userType.help': 'Cela nous aide à recommander l\'outil adapté à votre niveau d\'utilisation',
    'option.professional': 'Professionnel',
    'option.amateur': 'Jardinier amateur',
  },
  de: {
    // General UI
    'app.title': 'FELCO Selektor',
    'app.subtitle': 'Finden Sie die perfekte FELCO-Gartenschere für Ihre Bedürfnisse',
    'app.footer': '© {year} FELCO. Alle Rechte vorbehalten.',
    'btn.back': 'Zurück',
    'btn.reset': 'Neu starten',
    'btn.viewProduct': 'Produkt ansehen',
    'language.select': 'Wählen Sie Ihre Sprache',
    'language.en': 'English',
    'language.fr': 'Français',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.es': 'Español',
    'language.it': 'Italiano',
    'results.perfect': 'Perfekte Übereinstimmung',
    'results.alternative': 'Alternative Produkte',
    'results.features': 'Eigenschaften:',
    'results.noMatch': 'Wir konnten keine perfekte Übereinstimmung für Ihre Kriterien finden:',
    'results.perfectMatch': 'Diese Produkte entsprechen perfekt all Ihren Anforderungen:',
    'results.noMatchText': 'Wir konnten keine Produkte finden, die perfekt zu all Ihren Kriterien passen.',
    'results.partialMatch': 'Diese Produkte passen zu Ihrer Händigkeit, entsprechen aber nicht vollständig allen Ihren Kriterien:',

    // Questions
    'question.handSize': 'Wie groß ist Ihre Hand?',
    'question.handSize.help': 'Wählen Sie die Option, die Ihre Handgröße am besten beschreibt',
    'option.small': 'Klein',
    'option.medium': 'Mittel',
    'option.large': 'Groß',
    
    'question.handedness': 'Sind Sie Rechtshänder oder Linkshänder?',
    'question.handedness.help': 'Wählen Sie, welche Hand Sie hauptsächlich zum Schneiden verwenden',
    'option.right': 'Rechtshänder',
    'option.left': 'Linkshänder',
    
    'question.usageType': 'Wofür werden Sie die Gartenschere hauptsächlich verwenden?',
    'question.usageType.help': 'Wählen Sie die Art des Schneidens oder Beschneidens aus, die Sie hauptsächlich durchführen werden',
    'option.arboristry_and_forestry': 'Baumpflege und Forstwirtschaft',
    'option.flowers_and_roses': 'Blumen und Rosen',
    'option.fruit_nut_trees_and_orchards': 'Obst-/Nussbäume und Obstgärten',
    'option.grapes_and_vineyards': 'Trauben und Weinberge',
    'option.hedges_and_bushes': 'Hecken und Büsche',
    'option.landscaping_and_municipalities': 'Landschaftsbau und Kommunen',
    'option.nurseries': 'Baumschulen',
    
    'question.userType': 'Sind Sie ein professioneller oder Hobby-Gärtner?',
    'question.userType.help': 'Dies hilft uns, das richtige Werkzeug für Ihr Anwendungsniveau zu empfehlen',
    'option.professional': 'Profi',
    'option.amateur': 'Hobbygärtner',
  },
  nl: {
    // General UI
    'app.title': 'FELCO Selector',
    'app.subtitle': 'Vind de perfecte FELCO snoeischaar voor uw behoeften',
    'app.footer': '© {year} FELCO. Alle rechten voorbehouden.',
    'btn.back': 'Terug',
    'btn.reset': 'Opnieuw beginnen',
    'btn.viewProduct': 'Product bekijken',
    'language.select': 'Selecteer uw taal',
    'language.en': 'English',
    'language.fr': 'Français',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.es': 'Español',
    'language.it': 'Italiano',
    'results.perfect': 'Perfecte match',
    'results.alternative': 'Alternatieve producten',
    'results.features': 'Kenmerken:',
    'results.noMatch': 'We konden geen perfecte match vinden voor uw criteria:',
    'results.perfectMatch': 'Deze producten voldoen perfect aan al uw eisen:',
    'results.noMatchText': 'We konden geen producten vinden die perfect passen bij al uw criteria.',
    'results.partialMatch': 'Deze producten passen bij uw handigheid, maar voldoen niet volledig aan al uw criteria:',

    // Questions
    'question.handSize': 'Wat is de grootte van uw hand?',
    'question.handSize.help': 'Kies de optie die uw handgrootte het beste beschrijft',
    'option.small': 'Klein',
    'option.medium': 'Gemiddeld',
    'option.large': 'Groot',
    
    'question.handedness': 'Bent u rechtshandig of linkshandig?',
    'question.handedness.help': 'Selecteer welke hand u voornamelijk gebruikt voor het snoeien',
    'option.right': 'Rechtshandig',
    'option.left': 'Linkshandig',
    
    'question.usageType': 'Waarvoor zult u de snoeischaar voornamelijk gebruiken?',
    'question.usageType.help': 'Selecteer het belangrijkste type knip- of snoeiwerk dat u gaat doen',
    'option.arboristry_and_forestry': 'Boomverzorging & Bosbouw',
    'option.flowers_and_roses': 'Bloemen & Rozen',
    'option.fruit_nut_trees_and_orchards': 'Fruit/Notenbomen & Boomgaarden',
    'option.grapes_and_vineyards': 'Druiven & Wijngaarden',
    'option.hedges_and_bushes': 'Heggen & Struiken',
    'option.landscaping_and_municipalities': 'Landschapsbeheer & Gemeenten',
    'option.nurseries': 'Kwekerijen',
    
    'question.userType': 'Bent u een professional of hobbytuinier?',
    'question.userType.help': 'Dit helpt ons het juiste gereedschap aan te bevelen voor uw gebruiksniveau',
    'option.professional': 'Professional',
    'option.amateur': 'Hobbytuinier',
  },
  es: {
    // General UI
    'app.title': 'Selector FELCO',
    'app.subtitle': 'Encuentre las tijeras de podar FELCO perfectas para sus necesidades',
    'app.footer': '© {year} FELCO. Todos los derechos reservados.',
    'btn.back': 'Atrás',
    'btn.reset': 'Empezar de nuevo',
    'btn.viewProduct': 'Ver Producto',
    'language.select': 'Seleccione su idioma',
    'language.en': 'English',
    'language.fr': 'Français',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.es': 'Español',
    'language.it': 'Italiano',
    'results.perfect': 'Coincidencia Perfecta',
    'results.alternative': 'Productos Alternativos',
    'results.features': 'Características:',
    'results.noMatch': 'No pudimos encontrar una coincidencia perfecta para sus criterios:',
    'results.perfectMatch': 'Estos productos coinciden perfectamente con todos sus requisitos:',
    'results.noMatchText': 'No pudimos encontrar productos que coincidan perfectamente con todos sus criterios.',
    'results.partialMatch': 'Estos productos coinciden con su mano dominante pero no coinciden completamente con todos sus criterios:',

    // Questions
    'question.handSize': '¿Cuál es el tamaño de su mano?',
    'question.handSize.help': 'Elija la opción que mejor describa el tamaño de su mano',
    'option.small': 'Pequeña',
    'option.medium': 'Mediana',
    'option.large': 'Grande',
    
    'question.handedness': '¿Es usted diestro o zurdo?',
    'question.handedness.help': 'Seleccione qué mano usa principalmente para podar',
    'option.right': 'Diestro',
    'option.left': 'Zurdo',
    
    'question.usageType': '¿Para qué utilizará principalmente las tijeras de podar?',
    'question.usageType.help': 'Seleccione el tipo principal de corte o poda que realizará',
    'option.arboristry_and_forestry': 'Arboricultura y Silvicultura',
    'option.flowers_and_roses': 'Flores y Rosas',
    'option.fruit_nut_trees_and_orchards': 'Árboles Frutales/de Frutos Secos y Huertos',
    'option.grapes_and_vineyards': 'Uvas y Viñedos',
    'option.hedges_and_bushes': 'Setos y Arbustos',
    'option.landscaping_and_municipalities': 'Paisajismo y Municipios',
    'option.nurseries': 'Viveros',
    
    'question.userType': '¿Es usted un profesional o un jardinero aficionado?',
    'question.userType.help': 'Esto nos ayuda a recomendar la herramienta adecuada para su nivel de uso',
    'option.professional': 'Profesional',
    'option.amateur': 'Jardinero aficionado',
  },
  it: {
    // General UI
    'app.title': 'Selettore FELCO',
    'app.subtitle': 'Trova le forbici da potatura FELCO perfette per le tue esigenze',
    'app.footer': '© {year} FELCO. Tutti i diritti riservati.',
    'btn.back': 'Indietro',
    'btn.reset': 'Ricomincia',
    'btn.viewProduct': 'Visualizza Prodotto',
    'language.select': 'Seleziona la tua lingua',
    'language.en': 'English',
    'language.fr': 'Français',
    'language.de': 'Deutsch',
    'language.nl': 'Nederlands',
    'language.es': 'Español',
    'language.it': 'Italiano',
    'results.perfect': 'Corrispondenza Perfetta',
    'results.alternative': 'Prodotti Alternativi',
    'results.features': 'Caratteristiche:',
    'results.noMatch': 'Non siamo riusciti a trovare una corrispondenza perfetta per i tuoi criteri:',
    'results.perfectMatch': 'Questi prodotti corrispondono perfettamente a tutti i tuoi requisiti:',
    'results.noMatchText': 'Non siamo riusciti a trovare prodotti che corrispondano perfettamente a tutti i tuoi criteri.',
    'results.partialMatch': 'Questi prodotti corrispondono alla tua mano dominante ma non corrispondono completamente a tutti i tuoi criteri:',

    // Questions
    'question.handSize': 'Qual è la dimensione della tua mano?',
    'question.handSize.help': 'Scegli l\'opzione che descrive meglio la dimensione della tua mano',
    'option.small': 'Piccola',
    'option.medium': 'Media',
    'option.large': 'Grande',
    
    'question.handedness': 'Sei destrorso o mancino?',
    'question.handedness.help': 'Seleziona quale mano usi principalmente per potare',
    'option.right': 'Destrorso',
    'option.left': 'Mancino',
    
    'question.usageType': 'Per cosa userai principalmente le forbici da potatura?',
    'question.usageType.help': 'Seleziona il tipo principale di taglio o potatura che farai',
    'option.arboristry_and_forestry': 'Arboricoltura e Silvicoltura',
    'option.flowers_and_roses': 'Fiori e Rose',
    'option.fruit_nut_trees_and_orchards': 'Alberi da Frutto/Noci e Frutteti',
    'option.grapes_and_vineyards': 'Uva e Vigneti',
    'option.hedges_and_bushes': 'Siepi e Cespugli',
    'option.landscaping_and_municipalities': 'Paesaggistica e Comuni',
    'option.nurseries': 'Vivai',
    
    'question.userType': 'Sei un giardiniere professionale o amatoriale?',
    'question.userType.help': 'Questo ci aiuta a raccomandare lo strumento giusto per il tuo livello di utilizzo',
    'option.professional': 'Professionale',
    'option.amateur': 'Giardiniere amatoriale',
  }
}; 